import React, { useEffect, useState } from "react";
import config from "../../../config";
import { IncidentData } from "../../../utils/Incident/incidentTypes";
import ApiDropdown from "../../DropDown/Apidropdown";
import "./ForwardModal.css";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../../../utils/Security/authFetch";
import { getSignatureLines } from "../../../utils/emailSignature";

type Role = {
  roleId: number;
  roleName: string;
};

type Person = {
  id: number;
  name: string;
  email?: string;
};

type ForwardModalProps = {
  isOpen: boolean;
  data: IncidentData | null;
  onClose: () => void;
};

const ForwardModal: React.FC<ForwardModalProps> = ({
  isOpen,
  data,
  onClose,
}) => {
  const navigate = useNavigate();

  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [persons, setPersons] = useState<Person[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [selectedRoleName, setSelectedRoleName] = useState<string | null>(null);

  const storedUsername = localStorage.getItem("username");
  const [senderFullName, setSenderFullName] = useState<string | null>(null);

  const fetchFullNameByUsername = async (username: string) => {
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/users/fullname?username=${encodeURIComponent(
          username,
        )}`,
      );

      if (res.ok) {
        const fullName = await res.text(); // endpoint returns String
        return fullName?.trim() || null;
      }

      return null;
    } catch (e) {
      console.error("Failed to fetch full name:", e);
      return null;
    }
  };
  useEffect(() => {
    if (isOpen) {
      setSelectedRole(null);
      setSelectedPersonId(null);
      setSelectedPerson(null);
      setPersons([]);
      const u = localStorage.getItem("username");
      if (u) {
        fetchFullNameByUsername(u).then((name) => setSenderFullName(name));
      } else {
        setSenderFullName(null);
      }
    }
  }, [isOpen]);

  // 🔹 Fetch persons when role changes
  const handleRoleChange = async (roleId: number) => {
    setSelectedRole(roleId);
    setSelectedPersonId(null);
    setSelectedPerson(null);

    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/persons/role/${roleId}`,
      );
      if (res.ok) {
        const data: Person[] = await res.json();
        setPersons(data);
      }

      const roleRes = await authFetch(
        `${config.apiBaseUrl}/api/roles/${roleId}`,
      );
      if (roleRes.ok) {
        const roleData: Role = await roleRes.json();
        setSelectedRoleName(roleData.roleName);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // 🔹 Select one person
  const handlePersonChange = async (personId: number) => {
    setSelectedPersonId(personId);

    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/persons/${personId}`,
      );
      if (res.ok) {
        const person: Person = await res.json();
        setSelectedPerson(person);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // 🔹 Main forward logic
  // 🔹 Main forward logic
  const handleForward = async () => {
    console.log("🚀 handleForward triggered");

    if (!data || !data.ticketId || !selectedRole) {
      console.warn("❌ Missing required data:", {
        data,
        ticketId: data?.ticketId,
        selectedRole,
      });
      alert("Please select a role.");
      return;
    }

    try {
      let usersToForward: {
        userId: number;
        name: string;
        email?: string;
      }[] = [];

      console.log("📌 Selected Person:", selectedPerson);
      console.log("📌 Persons in Role:", persons);

      // ✅ CASE 1: PERSON SELECTED
      if (selectedPersonId && selectedPerson) {
        console.log("👤 Forwarding to SINGLE person");

        const email = selectedPerson.email || "";
        console.log("📧 Selected Person Email:", email);

        const idRes = await authFetch(
          `${config.apiBaseUrl}/api/users/id-by-email?email=${encodeURIComponent(
            email,
          )}`,
        );

        console.log("📡 userId response status:", idRes.status);

        if (idRes.ok) {
          const userId = await idRes.json();
          console.log("✅ Found userId:", userId);

          usersToForward.push({
            userId,
            name: selectedPerson.name,
            email: selectedPerson.email,
          });
        } else {
          console.error("❌ Failed to fetch userId for selected person");
        }
      } else {
        // ✅ CASE 2: NO PERSON → ALL TEAM
        console.log("👥 No person selected → forwarding to ALL team");

        const users = await Promise.all(
          persons.map(async (p, index) => {
            console.log(`➡️ Processing person [${index}]`, p);

            if (!p.email) {
              console.warn("⚠️ Skipping (no email):", p.name);
              return null;
            }

            const idRes = await authFetch(
              `${config.apiBaseUrl}/api/users/id-by-email?email=${encodeURIComponent(
                p.email,
              )}`,
            );

            console.log(
              `📡 userId fetch for ${p.name} → status:`,
              idRes.status,
            );

            if (idRes.ok) {
              const userId = await idRes.json();
              console.log(`✅ ${p.name} → userId:`, userId);

              return {
                userId,
                name: p.name,
                email: p.email,
              };
            } else {
              console.error(`❌ Failed for ${p.name}`);
            }

            return null;
          }),
        );

        usersToForward = users.filter((u) => u !== null) as any[];
      }

      console.log("📦 Final usersToForward:", usersToForward);

      if (usersToForward.length === 0) {
        console.warn("❌ No users found after processing");
        alert("No users found to forward.");
        return;
      }

      // 🔹 Duplicate for each user
      console.log("📄 Starting duplication...");
      for (const user of usersToForward) {
        console.log(`🔁 Duplicating for ${user.name} (ID: ${user.userId})`);

        const res = await authFetch(
          `${config.apiBaseUrl}/api/incidents/${data.ticketId}/duplicate/${user.userId}`,
          { method: "POST" },
        );

        console.log(`📡 Duplicate response for ${user.name}:`, res.status);

        if (!res.ok) {
          console.error(`❌ Duplicate failed for ${user.name}`);
        }
      }

      // 🔹 Forward API
      console.log("📤 Calling forward API...");
      const forwardRes = await authFetch(
        `${config.apiBaseUrl}/api/incidents/forward?ticketId=${data.ticketId}&role=${selectedRoleName}&person=${usersToForward
          .map((u) => u.name)
          .join(", ")}`,
        { method: "POST" },
      );
      const toNames = usersToForward.map((u) => u.name).join(", ");
      console.log("📡 Forward API status:", forwardRes.status);
      const isAllSite = localStorage.getItem("isAllSite") === "true";
      const isAllNetwork = localStorage.getItem("isAllNetwork") === "true";
      const isAllLink = localStorage.getItem("isAllLink") === "true";

      const siteInfo = isAllSite ? "All Sites" : (data.affectedSite ?? "");
      const networkInfo = isAllNetwork
        ? "All Networks"
        : (data.affectedNetwork ?? "");
      const linkInfo = isAllLink ? "All Links" : (data.affectedLink ?? "");
      // 🔹 Emails
      const emails = usersToForward.map((u) => u.email).filter((e) => e);

      console.log("📧 Emails to send:", emails);

      if (emails.length > 0) {
        const emailRes = await authFetch(
          `${config.apiBaseUrl}/api/email/send`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              to: emails,
              subject: `Incident ${data.ticketId} forwarded`,
              body: `
<div style="font-family: Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.6;">
  <p>Hello,</p>
  <p>This Ticket was forwarded to you with the following details:</p>

  <strong>From:</strong> ${senderFullName ?? storedUsername ?? "System"}<br><br>
  <strong>To:</strong> ${toNames}<br><br>

  <strong>Customer:</strong> ${data.affectedCustomer ?? ""}<br><br>
  <strong>Network:</strong> ${networkInfo}<br><br>
  <strong>Site:</strong> ${siteInfo}<br><br>
  <strong>Link:</strong> ${linkInfo}<br><br>

  <strong>Incident Start Date:</strong> ${data.incidentStartDatetime ?? ""}<br><br>
  <strong>Incident Close Date:</strong> ${data.incidentCloseDatetime ?? ""}<br><br>

  <strong>Summary:</strong> ${data.summary ?? ""}<br><br>
  <strong>Description:</strong> ${data.description ?? ""}<br><br>
  <strong>Error Type:</strong> ${data.errorType ?? ""}<br><br>
  <strong>Closure Code:</strong> ${data.closureCode ?? ""}<br><br>
  <strong>Solution:</strong> ${data.solution ?? ""}<br><br>

  <p>
    <a href="https://bdm.bcomsat.com/incident" target="_blank"
       style="color: #2a1364; font-weight: bold; text-decoration: none;">
       👉 View Incident Details
    </a>
  </p>

  <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 20px;">
    <tr>
      <td style="vertical-align: top; padding-right: 12px;">
        <img src="cid:seslogo" alt="SES Logo" style="height:50px;width:auto;" />
      </td>

      <td>
        <div style="
          padding: 12px;
          border-left: 4px solid #2a1364;
          background-color: #f9f9f9;
          color: #2a1364;
          font-size: 13px;
        ">
          <strong>Managed Services Operations</strong><br>
          ${getSignatureLines(data.affectedCustomer, "#2a1364")}
        </div>
      </td>
    </tr>
  </table>
</div>
`,
            }),
          },
        );

        console.log("📡 Email API status:", emailRes.status);
      } else {
        console.warn("⚠️ No emails to send");
      }

      // 🔹 Journal

      console.log("📝 Saving journal message...");
      const journalRes = await authFetch(`${config.apiBaseUrl}/api/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: storedUsername,
          message: `Incident ${data.ticketId} forwarded to ${usersToForward
            .map((u) => u.name)
            .join(", ")}`,
          date: new Date().toISOString(),
          ticketId: data.ticketId,
        }),
      });

      console.log("📡 Journal API status:", journalRes.status);

      console.log("✅ Forward process completed successfully");

      alert("Forwarded successfully!");
      onClose();
      navigate("/incident");
      window.location.reload();
    } catch (err) {
      console.error("💥 Error in handleForward:", err);
      alert("Error forwarding incident.");
    }
  };
  if (!isOpen) return null;

  return (
    <div className="forward-modal-overlay">
      <div className="forward-modal-content">
        <div className="forward-modal-header">
          <h2 style={{ color: "white" }}>Forward Incident</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="forward-modal-body">
          <p>
            Forwarding incident with ID: <strong>{data?.ticketId}</strong>
          </p>

          <ApiDropdown<Role>
            api="roles"
            labelKey="roleName"
            valueKey="roleId"
            label="Select Role"
            onChange={(val) => handleRoleChange(Number(val))}
          />

          <ApiDropdown<Person>
            api=""
            labelKey="name"
            valueKey="id"
            label="Select Person (optional)"
            onChange={(val) => handlePersonChange(Number(val))}
            value={selectedPersonId ?? undefined}
            options={persons}
          />
        </div>

        <div className="forward-modal-footer">
          <button className="forward-button" onClick={handleForward}>
            Forward
          </button>
          <button className="action-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForwardModal;
